// AskList.jsx
import React, { useEffect, useMemo, useState } from "react";
import HomeButton from "./Homebutton";

const API_URL = "https://geoapi2s.s100lab.com";
const API_ASK_URL = API_URL + "/api/geo/service/asks";

export default function AskList() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const totalPages = useMemo(
    () => (size > 0 ? Math.max(1, Math.ceil(total / size)) : 1),
    [total, size]
  );

  useEffect(() => {
    let abort = false;
    const fetchList = async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(`${API_ASK_URL}?page=${page}&size=${size}`, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          let msg = "";
          try {
            const t = await res.json();
            msg = t?.error || t?.message || "";
          } catch { }
          throw new Error(msg || `요청 실패 (status: ${res.status})`);
        }
        const data = await res.json();
        if (!abort) {
          setItems(Array.isArray(data?.items) ? data.items : []);
          setTotal(Number(data?.total || 0));
        }
      } catch (e) {
        if (!abort) setErr(e.message || "목록을 불러오지 못했습니다.");
      } finally {
        if (!abort) setLoading(false);
      }
    };
    fetchList();
    return () => {
      abort = true;
    };
  }, [page, size]);

  // 생성시각 KST(Asia/Seoul) 포맷터
  const fmtKST = (iso) => {
    try {
      return new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const onRefresh = () => {
    // 같은 페이지/사이즈로 강제 새로고침: page 값을 한번 흔들어준다
    setPage((p) => p);
  };

  return (
    <div className="container" style={{ paddingTop: 24, paddingBottom: 24 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0">문의 목록 (ASK)  <span style={{ float: "inline-end" }}>
          <HomeButton />
        </span></h3>
        <div className="d-flex gap-2">
          <select
            className="form-select"
            style={{ width: 120 }}
            value={size}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(1);
            }}
            disabled={loading}
          >
            <option value={10}>10개씩</option>
            <option value={20}>20개씩</option>
            <option value={50}>50개씩</option>
          </select>
          <button
            className="btn btn-outline-secondary"
            onClick={onRefresh}
            disabled={loading}
          >
            새로고침
          </button>
        </div>
      </div>

      {err && (
        <div className="alert alert-danger" role="alert">
          {err}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: 40 }}>ID</th>
              <th style={{ width: 70 }}>이름</th>
              <th style={{ width: 150 }}>이메일</th>
              <th>문의 내용</th>
              <th style={{ width: 220 }}>생성 시각(KST)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  불러오는 중...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  표시할 항목이 없습니다.
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.id}>
                  <td>{it.id}</td>
                  <td>{it.name}</td>
                  <td>
                    <a href={`mailto:${it.email}`}>{it.email}</a>
                  </td>
                  <td title={it.ask} style={{ maxWidth: 520, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {it.ask}
                  </td>
                  <td>{fmtKST(it.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          총 {total.toLocaleString()}건 · {page}/{totalPages}페이지
        </div>
        <div className="btn-group">
          <button
            className="btn btn-outline-primary"
            onClick={() => setPage(1)}
            disabled={loading || page <= 1}
            title="첫 페이지"
          >
            «
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={loading || page <= 1}
            title="이전"
          >
            ‹
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={loading || page >= totalPages}
            title="다음"
          >
            ›
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => setPage(totalPages)}
            disabled={loading || page >= totalPages}
            title="마지막 페이지"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}
